import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import validate from "validate.js";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  generateAIRootCause,
  saveAiGeneratedQuestions,
  getIncidentById,
  saveRootCauseAnalysis,
  generateAIRootCauseSummary,
  getIncidentInstructions,
  saveRootCauseSummary,
} from "actions";
import RootCauseSummary from "./component/RootCauseSummary";
import RootCauseQA from "./component/RootCauseQA";
import RootCauseConfirmationModal from "./component/RootCauseConfirmationModal";

const schema = {
  incident_name: {
    presence: { allowEmpty: false, message: "^Incident Title is required" },
  },
  incident_datetime_formatted: {
    presence: { allowEmpty: false, message: "^Reported Date Time is required" },
  },
  turn_around_time: {
    presence: { allowEmpty: true, message: "^Turn around time is required" },
  },
};

const stripHtml = (htmlString) => {
  const div = document.createElement("div");
  div.innerHTML = htmlString;
  return div.textContent || div.innerText || "";
};

const RootCause = ({
  incidentId,
  previousData,
  setPreviousData,
  goToNextTab,
  goToPreviousTab,
  isFinalReportGenerated,
}) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const incidentState = useSelector((state) => state.incidentState);

  const incidentInstructions = useSelector(
    (state) => state.incidentState.incidentInstructionsList
  );

  // Use previousData.root_causes if present, else fallback to Redux
  const rootCauses =
    (previousData?.root_causes && previousData.root_causes.length > 0
      ? previousData.root_causes
      : incidentState.incidentRecord?.root_causes) || [];

  const [rootCauseId, setRootCauseId] = useState(null);
  const [questions, setQuestions] = useState(["", "", "", "", ""]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [answers, setAnswers] = useState(["", "", "", "", ""]);
  const [summary, setSummary] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      object_viewed_id: session.current_page_permissions?.object_id || "",
    },
    touched: {
      object_viewed_id: true,
    },
    errors: {},
  });

  // Fetch instructions
  useEffect(() => {
    dispatch(getIncidentInstructions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!previousData && incidentId) {
      dispatch(getIncidentById(incidentId, "update")).then((action) => {
        const data = action?.payload || action;
        if (data) {
          setPreviousData(data);
        }
      });
    }
  }, [incidentId, previousData, setPreviousData]);
  // Populate from previousData/rootCauses if present
  useEffect(() => {
    let latestRootCause = null;
    // Prefer previousData.root_causes if present and non-empty
    const rootCauseList =
      previousData?.root_causes && previousData.root_causes.length > 0
        ? previousData.root_causes
        : rootCauses;

    // Find the first root cause with a summary, otherwise fallback to last
    if (rootCauseList && rootCauseList.length > 0) {
      latestRootCause =
        rootCauseList.find((rc) => rc.summary) ||
        rootCauseList[rootCauseList.length - 1];
    }

    if (latestRootCause) {
      const latestQAString = latestRootCause.questions_answers;
      let parsedQA = [];
      try {
        parsedQA = latestQAString ? JSON.parse(latestQAString) : [];
      } catch (e) {
        console.error("Failed to parse questions_answers JSON:", e);
      }

      const extractedQuestions = parsedQA.map((qa) => qa.question || "");
      const extractedAnswers = parsedQA.map((qa) => qa.answer || "");

      const finalQuestions = [...extractedQuestions, "", "", "", "", ""].slice(
        0,
        5
      );
      const finalAnswers = [...extractedAnswers, "", "", "", "", ""].slice(
        0,
        5
      );

      setQuestions(finalQuestions);
      setAnswers(finalAnswers);
      setOriginalQuestions(finalQuestions);

      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          id: latestRootCause?.id || "",
        },
        touched: {
          ...prevState.touched,
          id: true,
        },
      }));

      setRootCauseId(latestRootCause?.id || null);
      setSummary(latestRootCause?.summary || "");
    }
  }, [previousData, rootCauses]);

  // Validate form on value change
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  // Generate AI Root Cause Questions
  const generateRootCause = async () => {
    const data = {
      incidentId: incidentId,
      incident_title: previousData.incident_name || "",
      // department: previousData.department?.name || "",
      incident_datetime: previousData.incident_datetime
        ? moment(previousData.incident_datetime + "Z").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : "",
      description: previousData.initiation_description
        ? stripHtml(previousData.initiation_description)
        : "",
      severity_level: previousData.severity_level_name || "",
      scope: Array.isArray(previousData.scopes)
        ? previousData.scopes.map((s) => s.opt_display || s.name)
        : [""],
      users: Array.isArray(previousData.users)
        ? previousData.users.map((u) => u.name)
        : [""],
      turn_around_time: previousData.turn_around_time || "",
      attachmentIds: Array.isArray(previousData.attachments)
        ? previousData.attachments.map((a) => a.id)
        : [],

      events: Array.isArray(previousData.events)
        ? previousData.events.map((e) => ({
            event_name: e.event_name,
            event_start_time: e.event_start_time,
            event_end_time: e.event_end_time,
            event_description: e.event_description || "",
          }))
        : [],

      resolutions: Array.isArray(previousData.resolutions)
        ? previousData.resolutions.map((r) => ({
            subject: r.subject,
            resolution_description: r.resolution_description || "",
          }))
        : [],
      prompt_instructions: incidentInstructions?.prompt_instructions || "",
    };

    try {
      const questions = await dispatch(generateAIRootCause(data));

      if (questions.length > 0 && incidentId) {
        const formattedQuestions = questions.map((q) => ({
          question: q,
          answer: "",
        }));

        const payload = {
          incident_id: incidentId,
          questions: formattedQuestions,
        };

        await dispatch(saveAiGeneratedQuestions(payload));
        //await dispatch(getIncidentById(incidentId, 'update'));
      }
    } catch (err) {
      console.error("Error in root cause generation flow", err);
    }
  };

  const handleQuestionEdit = (index, newValue) => {
    const updated = [...questions];
    updated[index] = newValue;
    setQuestions(updated);
  };
  const handleAnswerChange = (index, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index] = value;
    setAnswers(updatedAnswers);
  };

  const handleCancelEdit = (index) => {
    const updated = [...questions];
    updated[index] = originalQuestions[index];
    setQuestions(updated);
    setEditingIndex(null);
  };

  // Save Root Cause Analysis and Generate Summary
  const handleSubmit = async (event) => {
    event.preventDefault();

    setShowConfirmationModal(false);

    const finalQA = questions
      .map((q, index) => ({
        question: q,
        answer: answers[index] || "",
      }))
      .filter((pair) => pair.question);

    const rootCauseId = formState.values.id || "";

    const payload = {
      id: rootCauseId,
      qa_pairs: finalQA,
    };

    const summaryPayload = {
      questions_answers: finalQA,
      description: previousData.initiation_description || "",
      summary_instructions:
        incidentInstructions?.summary_instructions || "string",
      additional_parameters: {
        incident_datetime: previousData.incident_datetime
          ? moment(previousData.incident_datetime + "Z").format(
              "YYYY-MM-DD HH:mm:ss"
            )
          : "",
      },
    };

    try {
      // 1. Save QA pairs
      await dispatch(saveRootCauseAnalysis(payload));

      // 2. Generate summary
      const generatedSummaryAction = await dispatch(
        generateAIRootCauseSummary(summaryPayload)
      );
      const generatedSummary =
        generatedSummaryAction.payload || generatedSummaryAction;

      setSummary(generatedSummary);

      // 3. Save summary
      await dispatch(
        saveRootCauseSummary({
          id: rootCauseId,
          summary: generatedSummary,
        })
      );
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <>
      <div className="row">
        {!isFinalReportGenerated ? (
          <div className="col-md-8">
            <div className="d-flex justify-content-between mb-3">
              <div>
                <h1 className="font-20 font-weightr-bold heading-color mb-1">
                  Root Cause Analysis
                </h1>
              </div>
              <button className="add-new-btn" onClick={generateRootCause}>
                <img src="/assets/images/ai-icon.svg" className="me-2" alt="" />
                Generate
              </button>
            </div>

            {questions.filter((q) => q).length > 0 && (
              <>
                {questions.map((question, index) => (
                  <div className="mb-3" key={index}>
                    <div className="d-flex justify-content-between align-items-start">
                      <h2 className="font-14 font-weight-s-bold heading-color mb-2 w-100">
                        {`Why${index + 1}: `}
                        {editingIndex === index ? (
                          <Form.Control
                            type="text"
                            value={questions[index]}
                            onChange={(e) =>
                              handleQuestionEdit(index, e.target.value)
                            }
                            autoFocus
                            className="input-field w-100"
                          />
                        ) : (
                          question
                        )}
                      </h2>

                      {editingIndex === index ? (
                        <div className="d-flex ms-2">
                          <img
                            src="/assets/images/voice-save.svg"
                            width={20}
                            className="me-2 cursor-pointer"
                            title="Save"
                            alt="Save"
                            onClick={() => setEditingIndex(null)}
                          />
                          <img
                            src="/assets/images/voice-cancel.svg"
                            width={20}
                            className="cursor-pointer"
                            title="Cancel"
                            alt="Cancel"
                            onClick={() => handleCancelEdit(index)}
                          />
                        </div>
                      ) : (
                        <img
                          src="/assets/images/edit-icon.svg"
                          width={20}
                          className="ms-2 cursor-pointer"
                          onClick={() => setEditingIndex(index)}
                          title="Edit"
                          alt="Edit"
                        />
                      )}
                    </div>

                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Type here..."
                      className="input-field"
                      value={answers[index] || ""}
                      onChange={(e) =>
                        handleAnswerChange(index, e.target.value)
                      }
                    />
                  </div>
                ))}
              </>
            )}

            <div className="mt-3">
              <button
                className="n-u-btn"
                onClick={() => setShowConfirmationModal(true)}
              >
                Save Root Cause{" "}
              </button>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="col-md-4">
          <RootCauseSummary
            summary={summary}
            previousData={previousData}
            rootCauseId={rootCauseId}
            incidentId={incidentId}
            goToNextTab={goToNextTab}
            isFinalReportGenerated={isFinalReportGenerated}
          />
        </div>
        {isFinalReportGenerated && (
          <div className="col-md-8">
            {rootCauses.map((rc) => (
              <RootCauseQA
                key={rc.id}
                QA={JSON.parse(rc.questions_answers || "[]")}
                incidentId={incidentId}
              />
            ))}
          </div>
        )}
      </div>

      <RootCauseConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleSubmit}
        title="Save Root Cause Analysis"
        message="Generating a new Root Cause Analysis will overwrite the existing one and you will lose the previous results."
      />
    </>
  );
};

export default RootCause;
